import useStyles from 'isomorphic-style-loader/useStyles';
import { BadgeInfo } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { Popup } from 'semantic-ui-react';

import { renderSanitizedMarkdown } from './render-sanitized-markdown';
import s from './column-description.scss';

export interface ColumnDescriptionProps {
  /**
   * Tooltip content; a string renders as sanitized Markdown beside an info
   * icon, a ReactNode renders verbatim, and a falsy value renders nothing.
   */
  description?: ReactNode;
  /**
   * The header element the tooltip is anchored to.
   */
  trigger: ReactElement;
}

/**
 * Wraps a column header in a hover/focus tooltip describing the column, or
 * returns the header untouched when there is no description.
 */
export const ColumnDescription = ({
  description,
  trigger,
}: ColumnDescriptionProps): ReactElement => {
  useStyles(s);

  if (!description) {
    return trigger;
  }

  return (
    <Popup
      role="tooltip"
      className={s.descriptionPopup}
      content={
        typeof description === 'string' ? (
          <div className={s.descriptionContent}>
            <BadgeInfo className={s.descriptionIcon} />

            <div
              className={s.descriptionMarkdown}
              // eslint-disable-next-line react/no-danger -- Sanitized HTML from renderSanitizedMarkdown
              dangerouslySetInnerHTML={{
                __html: renderSanitizedMarkdown(description),
              }}
            />
          </div>
        ) : (
          description
        )
      }
      hideOnScroll
      mouseEnterDelay={400}
      on={['hover', 'focus']}
      // popperModifiers expects Popper placement strings, not Semantic's
      // `position` names. Keep fallbacks vertical only — horizontal placements
      // cover adjacent headers.
      popperModifiers={[
        {
          name: 'flip',
          options: {
            fallbackPlacements: [
              'top',
              'top-end',
              'bottom-start',
              'bottom',
              'bottom-end',
            ],
          },
        },
      ]}
      position="top left"
      trigger={trigger}
    />
  );
};

export default ColumnDescription;
